<template>
  <div
    class="border-b border-blue last:border-b-0 py-15 transition-opacity"
  >
    <div class="flex">
      <div class="mr-10 w-[80px] flex-[0_0_80px]">
        <a :href="productUrl">
          <img
            :src="imageSrc"
            :alt="imageAlt"
            width="80"
            height="96"
            loading="lazy"
            decoding="async"
            class="h-full w-full object-cover"
          />
        </a>
      </div>

      <div class="w-full">
        <div class="flex justify-between gap-5">
          <a :href="productUrl" class="text-blue hover:text-green">
            <h4 class="text-sm font-medium leading-tight" v-text="decodedTitle" />
          </a>

          <div class="ml-auto flex flex-col items-end text-right text-sm">
            <span class="font-semibold text-blue">
              {{ displayPrice }}
            </span>
            <span
              v-if="displayComparePrice"
              class="text-xs text-gray line-through"
              v-text="displayComparePrice"
            />
          </div>
        </div>

        <div class="mt-5 flex items-end justify-between gap-5">
          <template v-if="!showAddToCartButton">
            <div
              class="flex h-30 w-[100px] items-center justify-between border border-blue text-blue"
            >
              <button
                class="px-10 py-5"
                @click="update('decrement')"
                aria-label="Decrement"
              >
                <svg
                  width="10"
                  height="2"
                  viewBox="0 0 10 2"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0.8 1H9.8" stroke="currentColor" />
                </svg>
              </button>
              <span class="text-sm font-medium">{{ item.quantity }}</span>
              <button
                class="px-10 py-5"
                @click="update('increment')"
                aria-label="Increment"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0.8 5H9.5" stroke="currentColor" />
                  <path d="M5.15 0.4V9.95" stroke="currentColor" />
                </svg>
              </button>
            </div>

            <button
              class="text-xs uppercase text-blue"
              @click="remove"
              aria-label="Remove item"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
              >
                <line
                  x1="0.5"
                  y1="4.32108"
                  x2="14.5554"
                  y2="4.32108"
                  stroke="currentColor"
                  stroke-linecap="round"
                />
                <path
                  d="M6.76074 0.5H8.31543C9.14365 0.500241 9.81543 1.17172 9.81543 2V4.26074H5.26074V2C5.26074 1.17157 5.93232 0.5 6.76074 0.5Z"
                  stroke="currentColor"
                />
                <path
                  d="M2.42554 4.48648L2.87881 14.2675C2.90913 14.9219 2.92429 15.2491 3.08621 15.4844C3.16427 15.5978 3.26491 15.6939 3.38183 15.7666C3.62437 15.9174 3.9519 15.9174 4.60695 15.9174H10.3129C10.9565 15.9174 11.2782 15.9174 11.5186 15.7703C11.6345 15.6994 11.7347 15.6055 11.8132 15.4945C11.9758 15.2643 11.9969 14.9433 12.0392 14.3011L12.6855 4.48648"
                  stroke="currentColor"
                />
              </svg>
            </button>
          </template>

          <template v-else>
            <div
              v-if="upsellSizeOptions.length > 1"
              class="flex flex-nowrap gap-5 overflow-x-auto min-w-0"
            >
              <button
                v-for="size in upsellSizeOptions"
                :key="size.value"
                type="button"
                class="plp-size-pill plp-size-pill--sm shrink-0"
                :class="{'plp-size-pill--active': isActiveUpsellSize(size.value)}"
                @click="selectUpsellSize(size.value)"
                v-text="size.abbrev"
              />
            </div>
            <button
              class="button button_b !h-30 !min-w-0 !px-15 !text-sm shrink-0"
              @click="onAddToCart"
            >
              Add
            </button>
          </template>
        </div>

        <div v-if="sellingPlanName || computedPlans.length" class="mt-5">
          <select
            v-if="hasSellingPlan && computedPlans.length"
            :key="computedPlans.length"
            :value="currentSellingPlanId"
            @change="changePlan($event.target.value, $event.target.selectedIndex)"
            class="h-30 w-full border border-blue bg-white px-5 text-xs italic text-blue outline-none"
          >
            <option
              v-for="plan in computedPlans"
              :key="plan.id"
              :value="plan.id"
            >
              {{ plan.name }}
            </option>
          </select>

          <span
            v-else-if="sellingPlanName"
            class="text-xs font-semibold text-blue"
          >
            {{ sellingPlanName }}
          </span>

          <button
            v-if="!hasSellingPlan && computedPlans.length"
            :key="`upgrade-${computedPlans.length}`"
            class="mt-[3px] text-xs font-medium text-left  text-blue hover:text-green"
            @click="changePlan(computedPlans[0].id, 0)"
          >
            + Upgrade to subscription for free shipping
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState, mapActions} from "vuex";
import {formatMoney} from "@shopify/theme-currency";
import {dispatchCartErrorToast} from "@/utils/cart-error-toast";

export default {
  name: "CartItem",
  props: {
    item: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    showAddToCartButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedUpsellVariantId: null,
    };
  },
  created() {
    if (this.showAddToCartButton) {
      const variants = Array.isArray(this.item?.variants) ? this.item.variants : [];
      const defaultId = Number(this.item?.default_variant_id) || 0;
      const fromDefault = variants.find(v => Number(v.id) === defaultId);
      this.selectedUpsellVariantId =
        (fromDefault && fromDefault.id) || variants[0]?.id || null;
    }
  },
  computed: {
    ...mapState("cart", {
      cart: "cartData",
      loading: "loading",
      productsWithDetails: "productsWithDetails",
    }),
    productDetails() {
      return this.productsWithDetails?.[this.item.id];
    },
    matchedSubscribeItem() {
      const subscribeArray = window.Shopify?.products_subscribe || [];
      return subscribeArray.find(
        subscribeItem =>
          Array.isArray(subscribeItem.related) &&
          subscribeItem.related.some(
            relatedItem => relatedItem.id === this.item.product_id,
          ),
      );
    },
    productUrl() {
      if (this.matchedSubscribeItem?.handle) {
        return `/products/${this.matchedSubscribeItem.handle}`;
      }
      if (this.item.url) return this.item.url;
      if (this.item.handle) return `/products/${this.item.handle}`;
      return "#";
    },
    imageSrc() {
      const variantImage = this.selectedUpsellVariant?.image;
      if (variantImage) return variantImage;
      return (
        this.item.image ||
        this.item.featured_image?.url ||
        this.item.images?.[0]?.src ||
        ""
      );
    },
    imageAlt() {
      return this.item.featured_image?.alt || this.item.title || "";
    },
    decodedTitle() {
      return this.decodeHTML(this.item.title || "");
    },
    hasDiscount() {
      return Number(this.item.original_price) > Number(this.item.final_price);
    },
    // Per-unit price after cart-level (promo) discount is applied.
    // `final_price` is pre-promo; `total_discount` is the line's share of any
    // cart-level discount (in cents), so we subtract it across the quantity.
    effectiveUnitPrice() {
      const qty = Number(this.item.quantity) || 1;
      const finalLine = Number(this.item.final_line_price ?? this.item.final_price * qty) || 0;
      const totalDiscount = Number(this.item.total_discount) || 0;
      const effectiveLine = Math.max(finalLine - totalDiscount, 0);
      return effectiveLine / qty;
    },
    selectedUpsellVariant() {
      if (!this.showAddToCartButton) return null;
      const variants = Array.isArray(this.item?.variants) ? this.item.variants : [];
      return (
        variants.find(v => Number(v.id) === Number(this.selectedUpsellVariantId)) ||
        null
      );
    },
    upsellSizeOptions() {
      if (!this.showAddToCartButton) return [];
      const variants = Array.isArray(this.item?.variants) ? this.item.variants : [];
      if (variants.length < 2) return [];
      const seen = new Set();
      const options = [];
      variants.forEach(v => {
        const val = (v.option1 || "").trim();
        if (!val || seen.has(val)) return;
        seen.add(val);
        options.push({
          value: val,
          abbrev: this.abbreviateSize(val),
          variantId: v.id,
        });
      });
      return options;
    },
    displayPrice() {
      if (this.showAddToCartButton) {
        const variant = this.selectedUpsellVariant;
        if (variant && typeof variant.price === "number") {
          return formatMoney(variant.price, "${{amount}}");
        }
        if (typeof this.item.price === "string") return this.item.price;
        if (typeof this.item.price === "number") {
          return formatMoney(this.item.price, "${{amount}}");
        }
        return "";
      }
      return formatMoney(this.effectiveUnitPrice, "${{amount}}");
    },
    displayComparePrice() {
      if (this.showAddToCartButton) {
        const variant = this.selectedUpsellVariant;
        if (variant) {
          const vCmp = Number(variant.compare_at_price);
          const vPrice = Number(variant.price);
          if (Number.isFinite(vCmp) && vCmp > vPrice) {
            return formatMoney(vCmp, "${{amount}}");
          }
          return "";
        }
        const cmp = this.item.compare_at_price;
        if (!cmp) return "";
        const isString = typeof cmp === "string";
        const isZero = isString
          ? cmp.replace(/[^\d]/g, "") === "0"
          : Number(cmp) === 0;
        if (isZero) return "";
        const cmpFormatted = isString ? cmp : formatMoney(cmp, "${{amount}}");
        const isSame =
          this.item.price &&
          (typeof this.item.price === "string"
            ? this.item.price === cmp
            : Number(this.item.price) === Number(cmp));
        return isSame ? "" : cmpFormatted;
      }

      const effective = this.effectiveUnitPrice;
      const allocation = this.item.selling_plan_allocation || {};
      const compareCandidates = [
        this.item.original_price,
        allocation.compare_at_price,
        this.item.compare_at_price,
        this.item.final_price,
      ];
      for (const candidate of compareCandidates) {
        const num = Number(candidate);
        if (Number.isFinite(num) && num > effective) {
          return formatMoney(num, "${{amount}}");
        }
      }
      return "";
    },
    currentSellingPlanId() {
      return this.item?.selling_plan_allocation?.selling_plan?.id;
    },
    sellingPlanName() {
      return this.item?.selling_plan_allocation?.selling_plan?.name || "";
    },
    hasSellingPlan() {
      return !!this.item?.selling_plan_allocation;
    },
    computedPlans() {
      const plans = [];

      if (this.matchedSubscribeItem) {
        this.matchedSubscribeItem.related.forEach(relatedItem => {
          plans.push({
            id: relatedItem.selling_plan_groups?.[0]?.selling_plans?.[0]?.id,
            variantId: relatedItem.variants?.[0]?.id,
            name:
              relatedItem.title?.split("-")?.slice(1)?.join("-")?.trim() ||
              relatedItem.title,
            recurring_deliveries: relatedItem.recurring_deliveries,
            fromRelated: true,
          });
        });
        return plans;
      }

      const product = this.productDetails;
      if (!product?.selling_plan_groups) return [];

      product.selling_plan_groups.forEach(group => {
        group.selling_plans.forEach(plan => {
          plans.push({
            id: plan.id,
            name: plan.name,
            recurring_deliveries: plan.recurring_deliveries,
          });
        });
      });

      return plans;
    },
  },
  methods: {
    ...mapActions("cart", ["removeItem", "updateItem", "addItem", "updatePlan"]),
    /**
     * Only the error surface now. It used to refuse to run while a previous
     * action was still awaiting the server, which is what dropped the second
     * tap on a stepper — the store applies each change on screen immediately
     * and queues the requests, so there is nothing left to guard against.
     */
    async withProcessing(fn) {
      try {
        await fn();
      } catch (err) {
        dispatchCartErrorToast(this.$store, err);
      }
    },
    remove() {
      this.withProcessing(() => this.removeItem(this.item));
    },
    update(type) {
      this.withProcessing(() => this.updateItem({item: this.item, type}));
    },
    formatMoney(value) {
      return formatMoney(value, "${{amount}}");
    },
    decodeHTML(html) {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = html;
      return textarea.value;
    },
    async changePlan(planId, index) {
      if (!planId) return;
      if (Number(this.currentSellingPlanId) === Number(planId)) return;

      const selectedPlan = this.computedPlans[index];

      await this.withProcessing(() =>
        this.updatePlan({
          planId: Number(planId),
          item: {
            ...this.item,
            variant_id: selectedPlan?.fromRelated
              ? selectedPlan.variantId
              : this.item.variant_id,
          },
        }),
      );
    },
    abbreviateSize(size) {
      const map = {
        "extra small": "XS",
        mini: "XS",
        small: "S",
        medium: "M",
        large: "L",
        "extra large": "XL",
        "one size": "OS",
      };
      const lower = String(size).toLowerCase().trim();
      if (map[lower]) return map[lower];
      const numericMatch = lower.match(/^(\d+(?:\.\d+)?)\s*[a-z]*$/i);
      if (numericMatch) return numericMatch[1];
      const words = String(size)
        .trim()
        .split(/[\s\-\/\(\)]+/)
        .filter(w => w.length > 0);
      if (words.length >= 2) {
        return words.slice(0, 3).map(w => w[0].toUpperCase()).join("");
      }
      return String(size).substring(0, 3);
    },
    isActiveUpsellSize(value) {
      const variant = this.selectedUpsellVariant;
      return !!variant && (variant.option1 || "").trim() === value;
    },
    selectUpsellSize(value) {
      const variants = Array.isArray(this.item?.variants) ? this.item.variants : [];
      const match = variants.find(v => (v.option1 || "").trim() === value);
      if (match) this.selectedUpsellVariantId = match.id;
    },
    async onAddToCart() {
      const variants = Array.isArray(this.item?.variants) ? this.item.variants : [];
      const fallbackVariant = variants[0];
      const variant =
        variants.find(v => Number(v.id) === Number(this.selectedUpsellVariantId)) ||
        fallbackVariant;
      if (!variant?.id) return;

      await this.withProcessing(() =>
        this.addItem({
          id: variant.id,
          quantity: 1,
          // Built from the upsell shape, not Shopify's product JSON: here the
          // variant's image lives on `image` and both prices are already in
          // cents (see `helper-cart-upsell-products.liquid`). The variant image
          // is an empty string when it has none, so the product's stands in.
          _optimistic: {
            product_title: this.item.title,
            variant_title: variant.title,
            price: variant.price,
            compare_at_price: variant.compare_at_price,
            image: variant.image || this.item.image,
            handle: this.item.handle,
          },
        }),
      );
    },
  },
};
</script>
